import { ProductSchema } from "@/lib/form/schema"
import { ProductFormData } from "@/lib/form/types"

export function buildProductLineKey(productId: string, lineId: string): string {
  return `${productId}::${lineId}`
}

export function dedupeProductSchemas(products: ProductSchema[]): ProductSchema[] {
  const seen = new Set<string>()
  const dedupedProducts: ProductSchema[] = []

  for (let productIdx = products.length - 1; productIdx >= 0; productIdx -= 1) {
    const product = products[productIdx]
    const dedupedLines = []

    for (
      let lineIdx = product.productionLines.length - 1;
      lineIdx >= 0;
      lineIdx -= 1
    ) {
      const line = product.productionLines[lineIdx]
      const key = buildProductLineKey(product.id, line.id)

      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      dedupedLines.unshift(line)
    }

    if (!dedupedLines.length) {
      continue
    }

    dedupedProducts.unshift({
      ...product,
      productionLines: dedupedLines,
    })
  }

  return dedupedProducts
}

export function dedupeProductsWithForms(params: {
  products: ProductSchema[]
  formProducts: ProductFormData[]
}): {
  products: ProductSchema[]
  formProducts: ProductFormData[]
} {
  const seen = new Set<string>()
  const dedupedProducts: ProductSchema[] = []
  const dedupedFormProducts: ProductFormData[] = []

  for (let productIdx = params.products.length - 1; productIdx >= 0; productIdx -= 1) {
    const product = params.products[productIdx]
    const formProduct = params.formProducts[productIdx]
    const dedupedLines = []
    const dedupedFormLines = []

    for (
      let lineIdx = product.productionLines.length - 1;
      lineIdx >= 0;
      lineIdx -= 1
    ) {
      const line = product.productionLines[lineIdx]
      const key = buildProductLineKey(product.id, line.id)

      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      dedupedLines.unshift(line)
      if (formProduct?.productionLines?.[lineIdx]) {
        dedupedFormLines.unshift(formProduct.productionLines[lineIdx])
      }
    }

    if (!dedupedLines.length) {
      continue
    }

    dedupedProducts.unshift({
      ...product,
      productionLines: dedupedLines,
    })
    dedupedFormProducts.unshift({
      ...(formProduct || ({ id: product.id } as ProductFormData)),
      productionLines: dedupedFormLines,
    })
  }

  return {
    products: dedupedProducts,
    formProducts: dedupedFormProducts,
  }
}

export function filterProductsWithoutExistingLines(params: {
  existingProducts: ProductSchema[]
  incomingProducts: ProductSchema[]
}): ProductSchema[] {
  const existingKeys = new Set(
    params.existingProducts.flatMap((product) =>
      product.productionLines.map((line) =>
        buildProductLineKey(product.id, line.id)
      )
    )
  )

  return params.incomingProducts.flatMap((product) => {
    const productionLines = product.productionLines.filter(
      (line) => !existingKeys.has(buildProductLineKey(product.id, line.id))
    )

    if (!productionLines.length) {
      return []
    }

    return [
      {
        ...product,
        productionLines,
      },
    ]
  })
}

export function dedupeOfflineSyncSheets<
  TSheet extends { productId: string; lineId: string },
>(sheets: TSheet[]): TSheet[] {
  const seen = new Set<string>()
  const dedupedSheets: TSheet[] = []

  for (let sheetIdx = sheets.length - 1; sheetIdx >= 0; sheetIdx -= 1) {
    const sheet = sheets[sheetIdx]
    const key = buildProductLineKey(sheet.productId, sheet.lineId)

    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    dedupedSheets.unshift(sheet)
  }

  return dedupedSheets
}
